import { useState, useEffect, useRef, useCallback } from "react";
import axios, { API, TOKEN_KEY } from "../utils/api";

/** Sends the save with `keepalive: true` so it survives a tab close/refresh, unlike a normal axios/XHR call which can get cancelled mid-flight. */
function flushSave(payload) {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/api/user-data`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch { }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Retries an async request instead of silently giving up on the first network hiccup — a slow or
 * flaky connection shouldn't mean a save just vanishes with no sign anything went wrong.
 * Returns true once it actually succeeds, false if all attempts failed.
 *
 * The backoff schedule spans ~60s total, not just a couple of seconds — Render's free tier
 * spins down when idle and can take 10-30s+ to wake back up on the first request after a gap,
 * and that's a normal wake-up, not a real failure. A short retry window would flag that as
 * "failed" and show a scary banner right as the save was about to succeed on its own.
 */
async function requestWithRetry(fn, attempts = 6, delays = [2000, 4000, 8000, 14000, 20000]) {
  for (let i = 0; i < attempts; i++) {
    try {
      await fn();
      return true;
    } catch {
      if (i < attempts - 1) await sleep(delays[i] ?? 20000);
    }
  }
  return false;
}

const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;

/**
 * Trims a scan snapshot down to what the watchlist and alerts actually read
 * back later.
 *
 * A full scan is ~80 stocks each carrying candle arrays, four separate reason
 * lists and provenance metadata. Persisting all of that pushed the save past
 * the server's JSON body limit, so every /api/user-data PUT was rejected with
 * "request entity too large" and nothing saved at all. Only the fields the app
 * reads from a *stored* scan are kept; the full objects still live in memory
 * and localStorage for the current session.
 */
function slimScannedStocks(stocks) {
  if (!Array.isArray(stocks)) return [];
  return stocks.map((s) => ({
    symbol: s.symbol,
    name: s.name,
    sector: s.sector,
    price: s.price,
    change: s.change,
    // Session stats the detail view renders — dropping these made DAY HIGH,
    // DAY LOW and PREV CLOSE read N/A for every stock once a scan round-tripped
    // through a save.
    high: s.high,
    low: s.low,
    prevClose: s.prevClose,
    score: s.score,
    momentum: s.momentum,
    trend: s.trend,
    positionScore: s.positionScore,
    fundamentalsScore: s.fundamentalsScore,
    // Legacy field the UI still falls back to for snapshots saved before the
    // category split. Dropping it left those older entries rendering blank.
    volumeScore: s.volumeScore,
    technicalBias: s.technicalBias,
    biasConfidence: s.biasConfidence,
    tomorrowBias: s.tomorrowBias,
    fiveDayReturn: s.fiveDayReturn,
    twentyDayReturn: s.twentyDayReturn,
    sixtyDayReturn: s.sixtyDayReturn,
    earningsFlag: s.earningsFlag,
    insiderSignal: s.insiderSignal,
    riskReward: s.riskReward,
    /**
     * The verdict, not just the number behind it.
     *
     * Dropping setupGrade did not merely blank a label — gradeLabel() falls
     * back to score thresholds when it is missing, so a stock the server had
     * explicitly refused to call a TOP SETUP (stop too wide, no fundamentals,
     * already extended) was promoted to one the moment a scan round-tripped
     * through a save. The disqualifiers are kept for the same reason: they are
     * the evidence for the refusal, and a handful of short strings costs far
     * less than the candle arrays this function exists to strip.
     */
    setupGrade: s.setupGrade,
    setupDisqualifiers: s.setupDisqualifiers,
    dataCoverage: s.dataCoverage,
    // Peer context, kept for the same reason as setupGrade: it is part of the
    // verdict, and recomputing it client-side is not possible.
    volatilityClass: s.volatilityClass,
    peerPercentile: s.peerPercentile,
    peerSampleSize: s.peerSampleSize,
    peerLabel: s.peerLabel,
    missingInputs: s.missingInputs,
    hasFundamentals: s.hasFundamentals,
    fundamentalsEvaluated: s.fundamentalsEvaluated,
    // Provenance flags — the ● / ◆ markers in the table, and the "Dynamic"
    // sector filter in App.jsx, which silently matched nothing after a reload.
    isDynamic: s.isDynamic,
    isFMP: s.isFMP,
    // Numbers only — the provenance/meta block is intentionally dropped here.
    fundamentals: s.fundamentals
      ? {
          revenueTTM: s.fundamentals.revenueTTM,
          revenueGrowthYoy: s.fundamentals.revenueGrowthYoy,
          epsTTM: s.fundamentals.epsTTM,
          netProfitMargin: s.fundamentals.netProfitMargin,
          peRatio: s.fundamentals.peRatio,
          marketCap: s.fundamentals.marketCap,
          nextEarningsDate: s.fundamentals.nextEarningsDate,
        }
      : null,
  }));
}

function readLocal(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)); } catch { return fallback; }
}

/**
 * Owns watchlist, investments, and the prediction log — persisted to MongoDB (via /api/user-data)
 * with localStorage as an instant-load cache. The server's data wins on initial load *unless*
 * the user has already started editing before that response arrives, in which case their local
 * edit wins instead (see userEditedRef below) — otherwise a slow initial load (e.g. a cold-started
 * Render backend) could silently overwrite a fresh edit with stale server data.
 */
export function useUserData() {
  const [watchlist, setWatchlist]     = useState(() => readLocal("wl", []));
  const [investments, setInvestments] = useState(() => readLocal("ts_investments", {}));
  const [predLog, setPredLog]         = useState(() => readLocal("ts_pred_log", []));
  const [alerts, setAlerts]           = useState(() => readLocal("ts_alerts", {}));
  const [scannedStocks, setScannedStocks] = useState(() => readLocal("ts_scanned_stocks", []));
  const [lastScan, setLastScan]      = useState(() => {
    const v = readLocal("ts_last_scan", null);
    return v ? new Date(v) : null;
  });
  const [syncFailed, setSyncFailed] = useState(false);
  const loadedRef = useRef(false);
  const saveTimer = useRef(null);
  // The initial GET can take a while (Render's free tier spins down when idle and can take
  // 10-30s+ to wake up). If the user starts editing before it resolves, applying the server's
  // (older) response would silently stomp their fresh edit. Once they've touched anything,
  // stop letting this one-time initial load overwrite state — local edits win from then on.
  const userEditedRef = useRef(false);
  const markEdited = useCallback(() => { userEditedRef.current = true; }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get("/api/user-data")
      .then((res) => {
        if (cancelled || userEditedRef.current) return;
        const { watchlist: wl, investments: inv, predictions: preds, alerts: al, scannedStocks: scanned, lastScan: last } = res.data || {};
        if (wl) setWatchlist(wl);
        if (inv) setInvestments(inv);
        if (preds) setPredLog(preds);
        if (al) setAlerts(al);
        if (scanned && scanned.length) setScannedStocks(scanned);
        if (last) setLastScan(new Date(last));
      })
      .catch(() => {
        // Server/DB unreachable — keep working off the localStorage cache.
      })
      .finally(() => { if (!cancelled) loadedRef.current = true; });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { try { localStorage.setItem("wl", JSON.stringify(watchlist)); } catch { } }, [watchlist]);
  useEffect(() => { try { localStorage.setItem("ts_investments", JSON.stringify(investments)); } catch { } }, [investments]);
  useEffect(() => { try { localStorage.setItem("ts_pred_log", JSON.stringify(predLog)); } catch { } }, [predLog]);
  useEffect(() => { try { localStorage.setItem("ts_alerts", JSON.stringify(alerts)); } catch { } }, [alerts]);
  useEffect(() => { try { localStorage.setItem("ts_scanned_stocks", JSON.stringify(scannedStocks)); } catch { } }, [scannedStocks]);
  useEffect(() => { try { localStorage.setItem("ts_last_scan", JSON.stringify(lastScan)); } catch { } }, [lastScan]);

  const latestPayloadRef = useRef(null);
  const saveTokenRef = useRef(0);

  // watchlist/investments are deliberately NOT in this payload — see the targeted
  // watchlist/investment endpoints below for why.
  useEffect(() => {
    if (!loadedRef.current) return;
    const payload = { predictions: predLog, alerts, scannedStocks: slimScannedStocks(scannedStocks), lastScan };
    latestPayloadRef.current = payload;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const token = ++saveTokenRef.current;
      requestWithRetry(() => axios.put("/api/user-data", payload)).then((ok) => {
        if (saveTokenRef.current !== token) return; // a newer edit already superseded this save
        setSyncFailed(!ok);
      });
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [predLog, alerts, scannedStocks, lastScan]);

  const retrySync = useCallback(() => {
    if (!latestPayloadRef.current) return;
    const token = ++saveTokenRef.current;
    requestWithRetry(() => axios.put("/api/user-data", latestPayloadRef.current)).then((ok) => {
      if (saveTokenRef.current !== token) return;
      setSyncFailed(!ok);
    });
  }, []);

  // If the tab is closed, refreshed, or backgrounded before the debounce above fires,
  // the pending save would otherwise be silently lost. Flush it immediately instead.
  useEffect(() => {
    const flush = () => {
      if (!loadedRef.current || !latestPayloadRef.current) return;
      clearTimeout(saveTimer.current);
      flushSave(latestPayloadRef.current);
    };
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // watchlist/investments save immediately as targeted, atomic MongoDB operations (addToSet/pull/
  // set/unset on a single field) rather than going through the bulk debounced snapshot-overwrite
  // above. That bulk approach breaks with more than one tab or device open on the same account:
  // a stale tab resaving its own outdated full snapshot would silently wipe out an edit a
  // different, more current tab just made. A targeted mutation can't clobber unrelated fields no
  // matter which tab sends it or in what order they arrive.
  const toggleWL = useCallback((sym) => {
    markEdited();
    const removing = watchlist.includes(sym);
    setWatchlist((p) => removing ? p.filter((s) => s !== sym) : [...p, sym]);
    const token = ++saveTokenRef.current;
    // Un-starring removes the stock from the Watchlist tab entirely, including any tracked
    // investment — otherwise it silently stays in the list (still keyed by `investments`) and
    // un-starring looks like it "did nothing" even though the star itself toggled correctly.
    if (removing && investments[sym]) {
      setInvestments((p) => { const n = { ...p }; delete n[sym]; return n; });
    }
    const reqs = removing
      ? [() => axios.delete(`/api/user-data/watchlist/${encodeURIComponent(sym)}`), ...(investments[sym] ? [() => axios.delete(`/api/user-data/investments/${encodeURIComponent(sym)}`)] : [])]
      : [() => axios.post("/api/user-data/watchlist", { symbol: sym })];
    Promise.all(reqs.map((req) => requestWithRetry(req))).then((results) => {
      if (saveTokenRef.current !== token) return;
      setSyncFailed(results.some((ok) => !ok));
    });
  }, [markEdited, watchlist, investments]);

  /**
   * Starts tracking a trade. The scanner's view at entry (score, stop, target)
   * is captured alongside the price so the watchlist can later show how the
   * setup has evolved — score-at-entry vs score-now is what makes "setup
   * weakening" and the win-rate statistics meaningful.
   */
  const addInvestment = useCallback((symbol, entryPrice, context = {}) => {
    const price = Number(entryPrice);
    if (!price || price <= 0) return;
    markEdited();
    /**
     * How much money went in, if the user said. Optional: tracking a trade by
     * entry price alone still works exactly as before, it just has no cash
     * figures. Invalid or absent stays null rather than 0 — a position of
     * "$0.00" would report every move as breaking even.
     */
    const amount = Number(context.amountInvested);
    const amountInvested = Number.isFinite(amount) && amount > 0 ? amount : null;
    const record = {
      entryPrice:  price,
      amountInvested,
      addedAt:     Date.now(),
      entryScore:  Number.isFinite(context.entryScore)  ? context.entryScore  : null,
      stopPrice:   Number.isFinite(context.stopPrice)   ? context.stopPrice   : null,
      targetPrice: Number.isFinite(context.targetPrice) ? context.targetPrice : null,
      status:      "open",
      exitPrice:   null,
      closedAt:    null,
    };
    setInvestments((p) => ({ ...p, [symbol]: record }));
    const token = ++saveTokenRef.current;
    requestWithRetry(() => axios.put(`/api/user-data/investments/${encodeURIComponent(symbol)}`, {
      entryPrice: price,
      amountInvested,
      entryScore: record.entryScore,
      stopPrice: record.stopPrice,
      targetPrice: record.targetPrice,
    })).then((ok) => {
      if (saveTokenRef.current !== token) return;
      setSyncFailed(!ok);
    });
  }, [markEdited]);

  /** Records an exit price, converting an open position into a completed trade. */
  const closeTrade = useCallback((symbol, exitPrice) => {
    const price = Number(exitPrice);
    if (!price || price <= 0) return;
    markEdited();
    setInvestments((p) => {
      const existing = p[symbol];
      if (!existing) return p;
      return { ...p, [symbol]: { ...existing, status: "closed", exitPrice: price, closedAt: Date.now() } };
    });
    const token = ++saveTokenRef.current;
    requestWithRetry(() => axios.put(`/api/user-data/investments/${encodeURIComponent(symbol)}`, {
      entryPrice: Number(investments?.[symbol]?.entryPrice) || price,
      // Carried through explicitly: the server merges against the stored
      // record, but sending it keeps the closed trade's cash P/L intact even
      // if that record is ever rebuilt from this payload.
      amountInvested: investments?.[symbol]?.amountInvested ?? null,
      status: "closed",
      exitPrice: price,
    })).then((ok) => {
      if (saveTokenRef.current !== token) return;
      setSyncFailed(!ok);
    });
  }, [markEdited, investments]);

  const removeInvestment = useCallback((symbol) => {
    markEdited();
    setInvestments((p) => { const n = { ...p }; delete n[symbol]; return n; });
    const token = ++saveTokenRef.current;
    requestWithRetry(() => axios.delete(`/api/user-data/investments/${encodeURIComponent(symbol)}`)).then((ok) => {
      if (saveTokenRef.current !== token) return;
      setSyncFailed(!ok);
    });
  }, [markEdited]);

  /**
   * Change how much money a tracked position holds, without disturbing it.
   *
   * The amount could previously only be set while first adding a position, so
   * a trade tracked before the field existed — or one where the figure was
   * mistyped — could not be corrected except by deleting and re-adding it,
   * which threw away the entry date and the scanner's view at entry. This
   * patches the one field and leaves the rest of the record alone.
   */
  const setInvestedAmount = useCallback((symbol, amount) => {
    const value = Number(amount);
    const amountInvested = Number.isFinite(value) && value > 0 ? value : null;
    if (amountInvested === null) return;
    markEdited();
    setInvestments((p) => (p[symbol] ? { ...p, [symbol]: { ...p[symbol], amountInvested } } : p));
    const token = ++saveTokenRef.current;
    requestWithRetry(() => axios.put(`/api/user-data/investments/${encodeURIComponent(symbol)}`, {
      // entryPrice is required by the route; resending the stored one keeps the
      // server's merge on the existing record rather than creating a new one.
      entryPrice: Number(investments?.[symbol]?.entryPrice),
      amountInvested,
    })).then((ok) => {
      if (saveTokenRef.current !== token) return;
      setSyncFailed(!ok);
    });
  }, [markEdited, investments]);

  const setAlertsTracked = useCallback((updater) => { markEdited(); setAlerts(updater); }, [markEdited]);
  const setScannedStocksTracked = useCallback((updater) => { markEdited(); setScannedStocks(updater); }, [markEdited]);
  const setLastScanTracked = useCallback((updater) => { markEdited(); setLastScan(updater); }, [markEdited]);

  const updatePredictions = useCallback((stocks) => {
    markEdited();
    const now = Date.now();
    const priceMap = {};
    stocks.forEach((s) => { priceMap[s.symbol] = s.price; });

    setPredLog((prev) => {
      const resolved = prev.map((p) => {
        if (p.status !== "pending") return p;
        const cur = priceMap[p.symbol];
        if (!cur || now - p.timestamp < FIVE_DAYS_MS) return p;
        const won = (p.bias === "BULLISH" && cur > p.price) || (p.bias === "BEARISH" && cur < p.price);
        const ret = ((cur - p.price) / p.price) * 100 * (p.bias === "BEARISH" ? -1 : 1);
        return { ...p, status: won ? "win" : "loss", resolvedPrice: cur, resolvedDate: new Date().toLocaleDateString(), returnPct: +ret.toFixed(2) };
      });
      const pendingSyms = new Set(resolved.filter((p) => p.status === "pending").map((p) => p.symbol));
      const newPreds = stocks
        .filter((s) => s.tomorrowBias !== "NEUTRAL" && !pendingSyms.has(s.symbol))
        .map((s) => ({ id: `${s.symbol}_${now}`, symbol: s.symbol, name: s.name, bias: s.tomorrowBias, price: s.price, score: s.score, timestamp: now, date: new Date().toLocaleDateString(), status: "pending" }));
      return [...resolved, ...newPreds].slice(-300);
    });
  }, [markEdited]);

  return {
    watchlist, toggleWL,
    investments, addInvestment, removeInvestment, closeTrade, setInvestedAmount,
    predLog, updatePredictions,
    alerts, setAlerts: setAlertsTracked,
    scannedStocks, setScannedStocks: setScannedStocksTracked,
    lastScan, setLastScan: setLastScanTracked,
    syncFailed, retrySync,
  };
}
